import React from 'react';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ImgRestaurantPlaceholder from '@assets/images/restaurant-placeholder.jpeg';

type RestaurantCardProps = {
    data: Record<string, any>;
    onClick: () => void;
};

const formatRating = (ratings?: number) => {
    if (!ratings) return '4.0';
    return (ratings / 2).toFixed(1);
};

const getDeliveryLabel = (data: Record<string, any>) => {
    if (data?.deliveryTime) return data.deliveryTime;
    if (data?.deliveryMinutes) return `${data.deliveryMinutes} min`;
    return '30 min';
};

const RestaurantCard: React.FC<RestaurantCardProps> = ({ data, onClick }) => {
    if (!data) return null;

    const rating = formatRating(data.ratings);
    const deliveryLabel = getDeliveryLabel(data);
    const cuisine = data.foodType || data.type || 'Multi-cuisine';
    const hasOffer = Boolean(data.discountPercent || data.extraDiscountPercent);

    return (
        <article className="restaurant-card" onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick()}>
            <div className="restaurant-card__media">
                <img
                    src={data.image || ImgRestaurantPlaceholder}
                    alt={data.name}
                    loading="lazy"
                    className="restaurant-card__image"
                />
                <div className="restaurant-card__overlay" />

                {!!data.extraDiscountPercent && (
                    <span className="restaurant-card__badge restaurant-card__badge--flash">
                        Extra {data.extraDiscountPercent}% off
                    </span>
                )}

                {!!data.discountPercent && (
                    <span className="restaurant-card__badge restaurant-card__badge--offer">
                        <LocalOfferRoundedIcon fontSize="inherit" />
                        {data.discountPercent}% off
                        {!!data.discountUpto && <span> · upto ₹{data.discountUpto}</span>}
                    </span>
                )}

                <div className="restaurant-card__rating-pill">
                    <StarRoundedIcon fontSize="small" />
                    {rating}
                </div>
            </div>

            <div className="restaurant-card__body">
                <div className="restaurant-card__title-row">
                    <h3 className="restaurant-card__name">{data.name}</h3>
                    <ChevronRightRoundedIcon className="restaurant-card__arrow" />
                </div>

                <p className="restaurant-card__cuisine">{cuisine}</p>

                <div className="restaurant-card__meta">
                    <span className="restaurant-card__meta-item">
                        <AccessTimeRoundedIcon fontSize="inherit" />
                        {deliveryLabel}
                    </span>
                    {hasOffer && (
                        <span className="restaurant-card__meta-item restaurant-card__meta-item--offer">
                            <LocalOfferRoundedIcon fontSize="inherit" />
                            Offers available
                        </span>
                    )}
                </div>

                {data.address && (
                    <p className="restaurant-card__address">
                        <PlaceOutlinedIcon fontSize="inherit" />
                        {data.address}
                    </p>
                )}
            </div>
        </article>
    );
};

export default RestaurantCard;
